import { Box } from '@mui/material';
import { GridRowId, GridSortItem } from '@mui/x-data-grid-premium';
import CXDataGrid from 'common/components/cx-data-grid/cx-data-grid.component';
import { useDrawer } from 'common/hooks';
import {
  findSkillsSortedByRarity,
  GridCellItem,
  NoMatchFound,
  SkillFilterGridGroupingModel,
  SkillStatus,
  useSkillFilterGridColumns,
} from 'features/matching-engine/matching-engine.utils';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';
import {
  PAGE_SIZE,
  ROW_HEIGHT,
  SLOTS,
} from 'features/profile-data-grid/profile-data-grid.constants';
import { Skill } from 'features/skills';
import { useRef } from 'react';
import { useDataGridVirtualization } from 'libs/mui/data-grid/hooks/useDataGridVirtualization';
import { useMatchingEngineResults } from '../MatchingEngineSearchView';
import { useMatchingEngineGridState } from 'features/matching-engine/hooks/useMatchingEngineDataGridState';

const getSkillStatusLabel = (row: any, skill: any) => {
  const isVerified = row.verifiedPersonSkills.some((s: any) => s.id === skill.id);
  if (isVerified) {
    return row.lastFeedbackDate || SkillStatus.VERIFIED;
  }

  const isMaybe = row.personSkill.some((s: any) => s.id === skill.id);
  if (isMaybe) {
    return SkillStatus.MAYBE;
  }

  return SkillStatus.MISSING;
};

type SkillFilterGridProps = {
  people: any[];
  skills: any[];
  setSelectedRows: React.Dispatch<React.SetStateAction<GridRowId[]>>;
};

export const SkillFilterGrid: React.FC<SkillFilterGridProps> = ({
  people,
  skills,
  setSelectedRows,
}) => {
  const { openDrawer } = useDrawer();
  const anchorRef = useRef<HTMLDivElement>(null);
  const columns = useSkillFilterGridColumns();
  const { initialState, handleSortModelChange } = useMatchingEngineGridState();

  // We have another call to `useMatchingEngineResults` inside `MatchingEngineSearchView`,
  // and since _this_ component is nested inside the _other_, then calling this hook will actually
  // hit the cache of the parent one, without triggering a double call.
  const {
    isFetching,
    hasNextPage = false,
    fetchNextPage,
    total,
  } = useMatchingEngineResults();

  const { rowCount, fetchMoreRows } = useDataGridVirtualization({
    currentRowCount: people.length,
    totalRowCount: total,
    pageSize: PAGE_SIZE,
    isFetching,
    hasNextPage,
    fetchNextPage,
  });

  if (people.length == 0) return <NoMatchFound />;

  const skillsSortedByRarity = findSkillsSortedByRarity(skills, people);

  const columnGroupingModel = SkillFilterGridGroupingModel;

  const openShowPreviousFeedbackDrawer = (revieweeId: string, skill: Skill) => {
    openDrawer(DrawerIdEnum.PAST_SKILL_FEEDBACKS, {
      revieweeId,
      skill,
    });
  };

  const valueToRank = (val: string) => {
    if (!val) return -1;
    if (val === 'Missing') return 0;
    if (val === 'Maybe') return 1;
    return 2;
  };

  const skillColumns = skillsSortedByRarity.map((skill) => ({
    field: `skill_${skill.id}`,
    headerName: skill.name ?? 'Dynamic Skill',
    minWidth: 150,
    flex: 0,
    renderCell: ({ row }: any) => {
      const isVerified = row.verifiedPersonSkills.some((s: any) => s.id === skill.id);
      return (
        <GridCellItem
          readonly={!isVerified}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            if (!isVerified) return;

            return openShowPreviousFeedbackDrawer(row.id, skill);
          }}
          value={getSkillStatusLabel(row, skill)}
        />
      );
    },
    sortComparator: (v1: string, v2: string) => valueToRank(v2) - valueToRank(v1),
  }));

  const sortModel = skillColumns.map<GridSortItem>((col) => ({
    field: col.field,
    sort: 'desc',
  }));

  const targetIndex = columns.findIndex((col) => col.field === 'missing');
  const dynamicColumns = [
    ...columns.slice(0, targetIndex + 1),
    ...skillColumns,
    ...columns.slice(targetIndex + 1),
  ];
  const records = people.map((person) => {
    return {
      id: person.id,
      photo: person.photo,
      profileID: person.id,
      name: person.fullName,
      manager: person.managers?.manager,
      l2Manager: person.managers?.l2Manager,
      l3Manager: person.managers?.l3Manager,
      currentAllocationCriticality: person.allocationMetadata,
      division: person.division,
      department: person.department,
      location: person.workLocation,
      product: person.product,
      matchedSkillsTracker: {
        confirmed: person.verifiedSkills.length,
        maybe: person.maybeSkills.length,
        missing: person.missingCount,
      },
      skillFilters: skills,
      personSkill: person.skills,
      verifiedPersonSkills: person.verifiedSkills,
      lastFeedbackDate: person.lastFeedbackDate,
    };
  });

  const handleSelectionModelChange = (selectionModel: GridRowId[]) => {
    setSelectedRows(selectionModel);
  };

  return (
    <Box height={700} maxHeight={800} overflow="hidden">
      <div ref={anchorRef}></div>
      <CXDataGrid
        paginationMode="server"
        sortingMode="server"
        rowsLoadingMode="server"
        onSortModelChange={handleSortModelChange}
        initialState={{
          ...initialState, // from useMatchingEngineGridState
          columns: {
            columnVisibilityModel: {
              l2manager: false,
              l3manager: false,
              location: false,
              department: false,
              division: false,
              product: false,
            },
          },
          pinnedColumns: { left: ['__check__', 'name'] },
        }}
        rows={records}
        columns={dynamicColumns}
        rowHeight={ROW_HEIGHT}
        columnGroupingModel={columnGroupingModel}
        checkboxSelection
        rowCount={rowCount}
        onFetchRows={fetchMoreRows}
        loading={isFetching}
        onRowSelectionModelChange={handleSelectionModelChange}
        slots={SLOTS}
        slotProps={{
          panel: {
            anchorEl: anchorRef.current,
            placement: 'bottom-end' as const,
            popperOptions: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, ROW_HEIGHT * 2.25], // adjust if needed
                  },
                },
              ],
            },
          },
          footer: {
            displayedRowCount: total,
          },
        }}
        sx={{
          '& .MuiDataGrid-columnHeader--filledGroup[aria-label="Current Allocation"] .MuiDataGrid-columnHeaderTitleContainerContent, \
           .MuiDataGrid-columnHeader--filledGroup[aria-label="Skills"] .MuiDataGrid-columnHeaderTitleContainerContent':
            {
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            },
        }}
      />
    </Box>
  );
};
